import { useCallback, useEffect, useState } from 'react';
import { aed, api, plusDays, today, type Booking } from '../api';

/**
 * Bookings and dispatch.
 *
 * Own-fleet operation: one row per vehicle in a booking, so a three-car family
 * order is visibly one order with three positions to fill rather than three
 * bookings to correlate. Under a partner model this screen becomes routing and
 * margin — see api/app/services/availability/partner.py.
 *
 * Two things are surfaced prominently because getting them wrong is what the
 * guest actually notices: the language the booking was made in, and the
 * preferences the site promised to honour (no music, Arabic-speaking guide,
 * child seats, dietary requirements).
 */
/** The emirate as dispatch reads it, not as the API stores it. */
const EMIRATE_LABELS: Record<string, string> = {
  dubai: 'Dubai',
  sharjah: 'Sharjah',
  abu_dhabi: 'Abu Dhabi — long run, fee charged',
};

export default function Bookings() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(plusDays(today(), 14));
  const [status, setStatus] = useState('');
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .bookings({ from, to, status: status || undefined })
      .then(setBookings)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [from, to, status]);

  useEffect(load, [load]);

  const unassigned = (bookings ?? []).filter(
    (b) => b.status === 'confirmed' && b.assignments.some((a) => !a.vehicle || !a.guide),
  );

  return (
    <>
      <h1>Bookings</h1>
      <p className="sub">
        Each booking is one order. A multi-vehicle booking has one position per vehicle, and a
        convoy is only dispatched when every position is filled.
      </p>

      {unassigned.length > 0 && (
        <div className="warn">
          <strong>
            {unassigned.length} confirmed booking{unassigned.length === 1 ? '' : 's'} still
            {unassigned.length === 1 ? ' has' : ' have'} an unassigned vehicle or guide.
          </strong>{' '}
          {unassigned.map((b) => b.ref).join(', ')}
        </div>
      )}

      <div className="row card" style={{ marginBlockEnd: '1rem' }}>
        <label className="small muted" htmlFor="bf">
          From
        </label>
        <input id="bf" type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        <label className="small muted" htmlFor="bt">
          To
        </label>
        <input id="bt" type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
        <label className="small muted" htmlFor="bs">
          Status
        </label>
        <select id="bs" value={status} onChange={(e) => setStatus(e.currentTarget.value)}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
          <option value="no_show">No show</option>
        </select>
        <button onClick={load}>Refresh</button>
      </div>

      {error && <p className="err">{error}</p>}
      {!bookings && <p className="muted">Loading…</p>}
      {bookings && bookings.length === 0 && <p className="muted">No bookings in that window.</p>}

      {bookings && bookings.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Ref</th>
              <th>Date</th>
              <th>Safari</th>
              <th>Cars</th>
              <th>Lang</th>
              <th>Guest</th>
              <th>Paid</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <Row key={b.ref} booking={b} open={open === b.ref} onToggle={() => setOpen(open === b.ref ? null : b.ref)} onSaved={load} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function prefLabels(preferences: Record<string, unknown>): string[] {
  const out: string[] = [];
  if (preferences.no_music) out.push('No music');
  if (preferences.arabic_guide) out.push('Arabic-speaking guide');
  if (preferences.child_seats) out.push(`Child seats: ${String(preferences.child_seats)}`);
  if (preferences.dietary) out.push(`Dietary: ${String(preferences.dietary)}`);
  for (const [k, v] of Object.entries(preferences)) {
    if (!['no_music', 'arabic_guide', 'child_seats', 'dietary'].includes(k)) out.push(`${k}: ${String(v)}`);
  }
  return out;
}

function Row({
  booking,
  open,
  onToggle,
  onSaved,
}: {
  booking: Booking;
  open: boolean;
  onToggle: () => void;
  onSaved: () => void;
}) {
  const prefs = prefLabels(booking.preferences);
  const incomplete = booking.assignments.some((a) => !a.vehicle || !a.guide);

  return (
    <>
      <tr>
        <td className="num">
          <strong>{booking.ref}</strong>
        </td>
        <td className="num">
          {booking.date}
          <div className="small muted num">{booking.startsAtLocal}</div>
        </td>
        <td className="small">{booking.productName}</td>
        <td className="num">{booking.vehicles}</td>
        <td>
          {/* Surfaced deliberately: the confirmation, the WhatsApp message and
              the driver briefing all have to come out in this language. */}
          <span className={`pill${booking.locale === 'ar' ? ' ar' : ''}`}>{booking.locale}</span>
        </td>
        <td className="small">
          {booking.customerName}
          <div className="muted num">{booking.customerPhone}</div>
        </td>
        <td className="num small">
          {aed(booking.paidAed)}
          {booking.balanceAed > 0 && <div className="muted">due {aed(booking.balanceAed)}</div>}
        </td>
        <td>
          <span className={`pill ${booking.status}`}>{booking.status}</span>
          {booking.status === 'confirmed' && incomplete && (
            <div className="small err">needs dispatch</div>
          )}
        </td>
        <td>
          <button onClick={onToggle}>{open ? 'Close' : 'Open'}</button>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={9}>
            <div className="card grid">
              <div>
                <h2 style={{ marginBlockStart: 0 }}>Guest</h2>
                <p className="small" style={{ margin: 0 }}>
                  {booking.customerName} · {booking.customerEmail} ·{' '}
                  <span className="num">{booking.customerPhone}</span>
                </p>
                <p className="small" style={{ margin: '0.3rem 0' }}>
                  <strong>Pickup:</strong> {booking.pickupAddress}{' '}
                  <span className="muted">({EMIRATE_LABELS[booking.pickupEmirate]})</span>
                </p>
                {booking.infants > 0 && (
                  <p className="small" style={{ margin: '0.3rem 0' }}>
                    {/* Free, so they are not in the headcount that was charged —
                        but they take a seat, and may need a child seat. */}
                    <strong>Infants under 3:</strong> <span className="num">{booking.infants}</span> —
                    seats needed, not charged.
                  </p>
                )}
                {booking.notes && (
                  <p className="small" style={{ margin: '0.3rem 0' }}>
                    <strong>Notes:</strong> {booking.notes}
                  </p>
                )}
                {booking.addOns.length > 0 && (
                  <div className="warn small">
                    <strong>Add-ons to arrange:</strong>{' '}
                    {booking.addOns
                      .map((a) => `${a.quantity} × ${a.name} (${aed(a.lineAed)})`)
                      .join(' · ')}
                  </div>
                )}
                {prefs.length > 0 && (
                  <div className="warn small">
                    <strong>Promised on the booking:</strong> {prefs.join(' · ')}
                  </div>
                )}
                <p className="small muted" style={{ margin: '0.3rem 0 0' }}>
                  Free cancellation until{' '}
                  <span className="num">
                    {new Date(booking.freeCancellationUntil).toLocaleString('en-GB')}
                  </span>{' '}
                  {booking.freeCancellationOpen ? '(open)' : '(closed)'} · total{' '}
                  {aed(booking.totalAed)} · {booking.paymentMode}
                  {booking.discountAed > 0 && (
                    <>
                      {' '}
                      · last-minute discount given {aed(booking.discountAed)}
                    </>
                  )}
                  {booking.pickupFeeAed > 0 && <> · pickup fee {aed(booking.pickupFeeAed)}</>}
                </p>
              </div>

              <div>
                <h2>Dispatch</h2>
                <table>
                  <thead>
                    <tr>
                      <th>Car</th>
                      <th>Vehicle</th>
                      <th>Guide</th>
                      <th>Pickup note</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {booking.assignments.map((a) => (
                      <AssignRow key={a.position} ref_={booking.ref} assignment={a} onSaved={onSaved} />
                    ))}
                  </tbody>
                </table>
                <p className="small muted">
                  Vehicle and guide IDs come from the database. The API refuses a guide who is not
                  listed as Arabic-speaking on a booking that asked for one, and refuses a vehicle
                  already assigned to another booking on the same departure.
                </p>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AssignRow({
  ref_,
  assignment,
  onSaved,
}: {
  ref_: string;
  assignment: { position: number; vehicle: string | null; guide: string | null; pickupNote: string | null };
  onSaved: () => void;
}) {
  const [vehicleId, setVehicleId] = useState('');
  const [guideId, setGuideId] = useState('');
  const [note, setNote] = useState(assignment.pickupNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.assign(ref_, {
        position: assignment.position,
        ...(vehicleId ? { vehicleId: Number(vehicleId) } : {}),
        ...(guideId ? { guideId: Number(guideId) } : {}),
        ...(note !== (assignment.pickupNote ?? '') ? { pickupNote: note } : {}),
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td className="num">{assignment.position}</td>
      <td>
        {assignment.vehicle ? (
          <span className="pill confirmed">{assignment.vehicle}</span>
        ) : (
          <input
            type="number"
            placeholder="id"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.currentTarget.value)}
            style={{ width: '5rem' }}
            aria-label={`Vehicle id for car ${assignment.position}`}
          />
        )}
      </td>
      <td>
        {assignment.guide ? (
          <span className="pill confirmed">{assignment.guide}</span>
        ) : (
          <input
            type="number"
            placeholder="id"
            value={guideId}
            onChange={(e) => setGuideId(e.currentTarget.value)}
            style={{ width: '5rem' }}
            aria-label={`Guide id for car ${assignment.position}`}
          />
        )}
      </td>
      <td>
        <input
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
          placeholder="e.g. second hotel"
          aria-label={`Pickup note for car ${assignment.position}`}
        />
      </td>
      <td>
        <button disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Assign'}
        </button>
        {error && <div className="err small">{error}</div>}
      </td>
    </tr>
  );
}
